import { useState, useEffect, useMemo } from "react";
import { AddCandidateToJobModal } from "./AddCandidateToJobModal";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Search } from "lucide-react";
import { useAIAnalysisProgress } from "@/hooks/useAIAnalysisProgress";
import { isRunActive, useAITopMatches } from "@/hooks/jobs/useAITopMatches";
import { AdvancedSearchModal } from "@/components/jobs/AdvancedSearchModal";
import { AITopMatchesBackgroundProgress } from "@/components/jobs/aiTopMatches/AITopMatchesBackgroundProgress";
import { AITopMatchesButton } from "@/components/jobs/aiTopMatches/AITopMatchesButton";
import { AITopMatchesModal } from "@/components/jobs/aiTopMatches/AITopMatchesModal";
import { useFeatureFlag } from "@/providers/FeatureFlagsProvider";
import { SharedKanbanBoard } from "./SharedKanbanBoard";
import { useJobKanban } from "@/hooks/jobs/useJobKanban";
import { useJobDetail } from "@/hooks/jobs/useJobDetail";
import { useTranslation } from "react-i18next";
import { addCandidateToJob } from "@/components/jobs/kanban/utils/candidateOperations";
import { CandidateHiredWorkforceModal } from "@/components/candidates/CandidateHiredWorkforceModal";
import { KanbanCandidate } from "./types";


interface JobKanbanWrapperProps {
	jobId: string | undefined;
	jobSkills?: string[];
	refreshKey?: number;
}

export const JobKanbanWrapper = ({ jobId, jobSkills = [], refreshKey }: JobKanbanWrapperProps) => {
	const { t } = useTranslation();
	const { job, refetch } = useJobDetail(jobId);
	const { isCompleted: aiAnalysisCompleted, resetCompleted: resetAICompleted } = useAIAnalysisProgress(jobId, "job");
	const {
		isAddCandidateOpen,
		setIsAddCandidateOpen,
		handleAddCandidate,
		addMultipleCandidates,
		candidates,
		handleStageChange,
		refreshCandidates,
	} = useJobKanban(jobId);

	const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
	const [hiredMatch, setHiredMatch] = useState<KanbanCandidate | null>(null);
	// AI Top 25 replaces the vector-based Advanced Search when the flag is on
	const aiTopMatchesEnabled = useFeatureFlag("aiTopMatches");
	const [isAITopMatchesOpen, setIsAITopMatchesOpen] = useState(false);
	const existingCandidateIds = useMemo(() => candidates.map((c) => c.candidateId), [candidates]);

	const hasJobAiAnalysis = useMemo(() => {
		if (!job?.ai_analysis)
			return false;

		if (typeof job.ai_analysis !== "object")
			return false;

		return Object.keys(job.ai_analysis).length > 0;
	}, [job?.ai_analysis]);

	// Owned here (not by the pop-up) so a running search keeps being followed while the pop-up is closed
	const topMatches = useAITopMatches(jobId, aiTopMatchesEnabled && hasJobAiAnalysis);

	useEffect(() => {
		if (refreshKey && refreshKey > 0)
			refreshCandidates();
	}, [refreshKey]);

	useEffect(() => {
		if (!hasJobAiAnalysis && isAdvancedSearchOpen)
			setIsAdvancedSearchOpen(false);
	}, [hasJobAiAnalysis, isAdvancedSearchOpen]);

	useEffect(() => {
		if (!hasJobAiAnalysis && isAITopMatchesOpen)
			setIsAITopMatchesOpen(false);
	}, [hasJobAiAnalysis, isAITopMatchesOpen]);

	// When AI analysis completes, refetch job data to enable the Advanced Search button
	useEffect(() => {
		if (aiAnalysisCompleted) {
			refetch();
			resetAICompleted();
		}
	}, [aiAnalysisCompleted, refetch, resetAICompleted]);

	// Handle adding multiple candidates to the job
	const handleAddCandidatesToJob = async (candidateIds: string[]) => {
		if (!jobId) return;

		for (const candidateId of candidateIds) {
			await addCandidateToJob(jobId, candidateId);
		}

		// Refresh the kanban board
		window.location.reload(); // Simple refresh for now
	};

		return (
			<div className="w-full">
				<div className="flex justify-end mb-4 gap-2">
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<span className={!hasJobAiAnalysis ? "cursor-not-allowed" : ""}>
									{aiTopMatchesEnabled ? (
										<AITopMatchesButton
											run={topMatches.run}
											disabled={!hasJobAiAnalysis}
											onClick={() => hasJobAiAnalysis && setIsAITopMatchesOpen(true)}
										/>
									) : (
										<Button
											onClick={() => hasJobAiAnalysis && setIsAdvancedSearchOpen(true)}
											disabled={!hasJobAiAnalysis}
											className="bg-purple-600 hover:bg-purple-700 flex items-center gap-1 disabled:opacity-50 disabled:pointer-events-none"
										>
											<Search className="h-4 w-4" />
											<span>{t("jobs.kanban.advancedSearch") || "Advanced Search"}</span>
										</Button>
									)}
								</span>
							</TooltipTrigger>
							{!hasJobAiAnalysis && (
								<TooltipContent>
									{aiTopMatchesEnabled ? t("aiTopMatches.disabledTooltip") : t("jobs.kanban.advancedSearchDisabledTooltip")}
								</TooltipContent>
							)}
						</Tooltip>
					</TooltipProvider>
					<Button
						onClick={handleAddCandidate}
						className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90 flex items-center gap-1"
					>
						<Plus className="h-4 w-4" />
						<span>{t("jobs.kanban.addCandidate")}</span>
					</Button>
				</div>

			{aiTopMatchesEnabled && !isAITopMatchesOpen && topMatches.run && isRunActive(topMatches.run) && (
				<AITopMatchesBackgroundProgress run={topMatches.run} onOpen={() => setIsAITopMatchesOpen(true)} />
			)}

			<SharedKanbanBoard
				jobId={jobId}
				matches={candidates}
				onStageChange={handleStageChange}
				onRefresh={refreshCandidates}
				onMatchHired={(match) => setHiredMatch(match)}
			/>

			<AddCandidateToJobModal
				isOpen={isAddCandidateOpen}
				onClose={() => setIsAddCandidateOpen(false)}
				onAddCandidates={handleAddCandidatesToJob}
				jobId={jobId || ''}
				isLoading={false}
			/>

			{aiTopMatchesEnabled && hasJobAiAnalysis && jobId && (
				<AITopMatchesModal
					isOpen={isAITopMatchesOpen}
					onClose={() => setIsAITopMatchesOpen(false)}
					topMatches={topMatches}
					jobTitle={job?.title}
					companyName={job?.company?.name}
					existingCandidateIds={existingCandidateIds}
					onCandidatesAdded={refreshCandidates}
				/>
			)}

			{!aiTopMatchesEnabled && hasJobAiAnalysis && (
				<AdvancedSearchModal
					isOpen={isAdvancedSearchOpen}
					onClose={() => setIsAdvancedSearchOpen(false)}
					jobId={jobId}
					jobSkills={jobSkills}
					onAddMatches={addMultipleCandidates}
				/>
			)}

			{hiredMatch && (
				<CandidateHiredWorkforceModal
					isOpen={true}
					onClose={() => setHiredMatch(null)}
					candidateId={hiredMatch.candidateId}
					candidateName={hiredMatch.name}
				/>
			)}
		</div>
	);
};